/** @typedef {import('headlight-configurable-application').Pict} Pict */

const libBulmaMarkdown = require('./PictProvider-Bulma-Markdown.js');
const libUserMarkdown = require('./_UserMarkdown.js');

/**
 * The input extension behind InputType `MarkdownDisplay`: a read-only field that renders the
 * markdown held in its *value* -- a remark a user typed -- rather than static markdown authored in
 * the manifest.
 *
 * InputType `Markdown` cannot do this. Its templates gate the display div on the descriptor's
 * Content/Default, so a descriptor bound to a data address renders the "No Markdown content
 * available to display" notification and hides the content, and it carries no label.
 *
 * @class
 * @extends libBulmaMarkdown
 */
class CustomInputHandler extends libBulmaMarkdown
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		/** @type {any} */
		this.log;

		this._remarksCSSRegistered = false;
	}

	/**
	 * Renders the markdown for this input.
	 *
	 * A Content/Default in the descriptor is markdown we authored, so it keeps the trusted
	 * rendering the Markdown InputType has always given it. A value is markdown a user typed, so it
	 * goes through the hardened renderer instead -- see _UserMarkdown.js for what that changes.
	 *
	 * @param {Object} pInput - The input descriptor.
	 * @param {any} pValue - The input value.
	 * @return {String} - The rendered HTML.
	 */
	getInputContent(pInput, pValue)
	{
		const tmpHasAuthoredContent = (pInput && (((typeof(pInput.Content) === 'string') && pInput.Content) || ((typeof(pInput.Default) === 'string') && pInput.Default)));
		if (tmpHasAuthoredContent)
		{
			return super.getInputContent(pInput, pValue);
		}
		// Templates call this directly to render a remark (Sample Authorization's test summary), with no
		// MarkdownDisplay input on the form to register the stylesheet.
		this.registerRemarksCSS();
		return libUserMarkdown.renderUserMarkdown(pValue, this.log);
	}

	/**
	 * A remark is body copy, not page furniture. The template already wraps it in Bulma's `content`
	 * class, and the parent's title classes would blow an `# h1` in a remark up to 2.5rem inside a
	 * form field, so the heading sizing is left to the shared remarks stylesheet.
	 *
	 * @param {string} pContent - The rendered content.
	 * @return {string} - The content, unchanged.
	 */
	addBulmaFlavor(pContent)
	{
		return pContent;
	}

	/**
	 * Puts the shared remarks styling in the page. Done on first render rather than in the
	 * constructor, because providers are attached before the document is ready.
	 */
	registerRemarksCSS()
	{
		if (this._remarksCSSRegistered || !this.pict.CSSMap)
		{
			return;
		}
		this._remarksCSSRegistered = true;
		this.pict.CSSMap.addCSS('Headlight-Markdown-Remarks', libUserMarkdown.RemarksCSS, 500, 'Bulma-MarkdownDisplay');
		this.pict.CSSMap.injectCSS();
		libUserMarkdown.watchRemarksTruncation();
	}

	/**
	 * The parent only writes to the display element when there is content, which would leave the
	 * previous remark on screen after the value is cleared. Emptying it first keeps the field honest.
	 *
	 * @param {Object} pInput - The input descriptor.
	 * @param {any} pValue - The input value.
	 */
	clearDisplayWhenEmpty(pInput, pValue)
	{
		if (!this.getInputContent(pInput, pValue))
		{
			this.pict.ContentAssignment.assignContent(this.getContentDisplayHTMLID(pInput.Macro.RawHTMLID), '');
		}
	}

	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.registerRemarksCSS();
		this.clearDisplayWhenEmpty(pInput, pValue);
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.registerRemarksCSS();
		this.clearDisplayWhenEmpty(pInput, pValue);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}
}

module.exports = CustomInputHandler;
